import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "../../SiteLayout";
import "../../styles/thoughts.css";

// Architecture-toggle captions. The "all" view is the initial state on load.
// The trailing <span> portion is rendered dimmed (matches the original innerHTML markup).
const ARCH_CAPS = {
  all: {
    lead: "You work with one Shadow. ",
    span: "Shadow runs a crew. The crew runs on your team’s memory.",
  },
  reps: {
    lead: "As a rep: ",
    span: "the Prep Desk briefs you, the Live Desk works your screen on the call, the Back Office clears the follow-through. Shipping today.",
  },
  managers: {
    lead: "As a manager: ",
    span: "the Coaching Staff reads every call and brings risk, the coaching moment, and workload truth to Slack. On the roadmap.",
  },
  cxos: {
    lead: "As a CXO: ",
    span: "the Intelligence Desk turns every conversation into cited win/loss, market signal, and forecast evidence. On the roadmap.",
  },
};

// Surfaces/desks carry one or more audience tags (space-separated, mirroring the
// original data-p). An element is dimmed when a non-"all" panel is selected and the
// element is not tagged for it.
function dim(active, tags) {
  return active !== "all" && !tags.split(" ").includes(active) ? "dim" : "";
}
function dimBracket(active, b) {
  return active !== "all" && b !== active ? "dim" : "";
}

export default function SiteThoughts() {
  // arch toggle state — "all" is the exact initial state on load.
  const [arch, setArch] = useState("all");
  const rootRef = useRef(null);

  // Ports the <script> reveal IntersectionObserver and the graphFeed fact pusher
  // (setTimeout -> setInterval) into one ref-scoped, StrictMode-safe effect with
  // full cleanup. The arch toggle is handled declaratively via React state above.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const timers = [];
    const observers = [];

    // ---- reveal-on-scroll ----
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    root.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    observers.push(io);

    // ---- graph feed: periodically push captured-fact chips, keeping at most 3 ----
    const feed = root.querySelector("#graphFeed");
    if (feed) {
      const facts = [
        '<b>+ captured</b> budget confirmed at $48K',
        "<b>+ linked</b> Sarah Lin → economic buyer intro",
        '<b>+ captured</b> security review starts Friday',
        "<b>+ updated</b> renewal window: November",
        '<b>+ captured</b> "we need SSO before rollout"',
      ];
      let fi = 0;
      const pushFact = () => {
        const chip = document.createElement("div");
        chip.className = "feed-chip";
        chip.innerHTML = facts[fi % facts.length];
        feed.appendChild(chip);
        if (feed.children.length > 3) feed.removeChild(feed.firstChild);
        fi++;
      };
      const startId = setTimeout(() => {
        pushFact();
        const intervalId = setInterval(pushFact, 3400);
        timers.push(intervalId);
      }, 2400);
      timers.push(startId);
    }

    return () => {
      timers.forEach((id) => {
        clearTimeout(id);
        clearInterval(id);
      });
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  const cap = ARCH_CAPS[arch];

  return (
    <SiteLayout page="thoughts" title="Thoughts | Shadow" navVariant="flat">
      <div ref={rootRef}>
        <header className="hero" style={{ padding: "160px 0 30px" }}>
          <div className="wrap" style={{ textAlign: "center" }}>
            <div className="eyebrow indigo" style={{ justifyContent: "center" }}>Thoughts</div>
            <h1 style={{ fontSize: "clamp(38px,5.6vw,62px)", maxWidth: "840px", margin: "18px auto 0" }}>Where Shadow<br />is going.</h1>
            <p className="lede" style={{ margin: "22px auto 0" }}>This page is the road, not the product you install today. We publish it because we intend to be held to it.</p>
          </div>
        </header>

        <section id="architecture" style={{ padding: "50px 0 30px" }}>
          <div className="wrap">
            <div className="reveal">
              <div className="eyebrow indigo">How Shadow is built</div>
              <h2>The whole map.</h2>
              <p className="lede">Surfaces on top, one face, the crew, the memory underneath. Honest status on every part.</p>
            </div>
            <div className="arch-toggle reveal" style={{ marginTop: "34px" }}>
              <button className={arch === "all" ? "active" : undefined} onClick={() => setArch("all")}>The whole system</button>
              <button className={arch === "reps" ? "active" : undefined} onClick={() => setArch("reps")}>For reps</button>
              <button className={arch === "managers" ? "active" : undefined} onClick={() => setArch("managers")}>For managers</button>
              <button className={arch === "cxos" ? "active" : undefined} onClick={() => setArch("cxos")}>For CXOs</button>
            </div>
            <div className="arch reveal">
              <div className="arch-label">Where you meet Shadow</div>
              <div className="arch-surfaces">
                <div data-p="reps" className={dim(arch, "reps")}>Calls <span className="chip now">now</span></div>
                <div data-p="reps" className={dim(arch, "reps")}>Email <span className="chip next">next</span></div>
                <div data-p="managers cxos" className={dim(arch, "managers cxos")}>Slack <span className="chip future">roadmap</span></div>
                <div data-p="reps" className={dim(arch, "reps")}>Phone <span className="chip future">roadmap</span></div>
              </div>
              <div className="arch-label">One face</div>
              <div className="arch-shadow">
                <span className="arch-peek" aria-hidden="true">
                  <svg viewBox="0 0 44 16" width="38" height="14"><path d="M8 16 C 8 6, 13 1, 22 1 C 31 1, 36 6, 36 16 Z" fill="#0E0E0C" /><ellipse cx="17.5" cy="9" rx="2.4" ry="3" fill="#FFFFFF" /><ellipse cx="26.5" cy="9" rx="2.4" ry="3" fill="#FFFFFF" /></svg>
                </span>
                <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true"><path d="M4 27 C7 16, 11.5 16, 14.5 25 C17 32.5, 20 32.5, 22 26" fill="none" stroke="#FFFFFF" strokeWidth="4.4" strokeLinecap="round" /><line x1="32" y1="17" x2="41" y2="29" stroke="rgba(255,255,255,0.5)" strokeWidth="2.6" strokeLinecap="round" /><circle cx="32" cy="17" r="5" fill="#FFFFFF" /><circle cx="41" cy="29" r="4" fill="rgba(255,255,255,0.5)" /></svg>
                <span>Shadow</span>
              </div>
              <div className="arch-label">The crew · 26 specialist agents on five desks</div>
              <div className="arch-desks">
                <Link className={`desk ${dim(arch, "reps")}`} data-p="reps" to="/agents#prep"><div className="desk-h"><span className="desk-dot" style={{ background: "var(--red)" }}></span><span className="n">Prep Desk</span><span className="chip next">next</span></div><div className="desk-ags"><span className="ag-pill">Research</span><span className="ag-pill">Brief</span><span className="ag-pill">Relationship</span><span className="ag-pill">Moments</span><span className="ag-pill">Play</span></div></Link>
                <Link className={`desk ${dim(arch, "reps")}`} data-p="reps" to="/agents#live"><div className="desk-h"><span className="desk-dot" style={{ background: "var(--red)" }}></span><span className="n">Live Desk</span><span className="chip now">now · v1</span></div><div className="desk-ags"><span className="ag-pill">Context</span><span className="ag-pill">Discovery</span><span className="ag-pill">Objection</span><span className="ag-pill">Storyteller</span><span className="ag-pill">Stakeholder</span><span className="ag-pill">Agenda</span><span className="ag-pill">Signal</span><span className="ag-pill">Commitment</span></div></Link>
                <Link className={`desk ${dim(arch, "reps")}`} data-p="reps" to="/agents#back"><div className="desk-h"><span className="desk-dot" style={{ background: "var(--red)" }}></span><span className="n">Back Office</span><span className="chip now">now · v1</span></div><div className="desk-ags"><span className="ag-pill">Scribe</span><span className="ag-pill">CRM</span><span className="ag-pill">Follow-up</span><span className="ag-pill">Dispatcher</span></div></Link>
                <Link className={`desk ${dim(arch, "managers")}`} data-p="managers" to="/agents#coaching"><div className="desk-h"><span className="desk-dot" style={{ background: "var(--blue)" }}></span><span className="n">Coaching Staff</span><span className="chip future">roadmap</span></div><div className="desk-ags"><span className="ag-pill">Coach</span><span className="ag-pill">Workload</span><span className="ag-pill">Risk</span><span className="ag-pill">Forecast</span><span className="ag-pill">Ramp</span></div></Link>
                <Link className={`desk ${dim(arch, "cxos")}`} data-p="cxos" to="/agents#intel"><div className="desk-h"><span className="desk-dot" style={{ background: "var(--green)" }}></span><span className="n">Intelligence Desk</span><span className="chip future">roadmap</span></div><div className="desk-ags"><span className="ag-pill">Win/Loss</span><span className="ag-pill">Market</span><span className="ag-pill">Voice of Customer</span><span className="ag-pill">Board</span></div></Link>
              </div>
              <div className="arch-brackets">
                <div className={`bracket reps ${dimBracket(arch, "reps")}`} data-b="reps">FOR REPS · SHIPPING NOW</div>
                <div className={`bracket managers ${dimBracket(arch, "managers")}`} data-b="managers">FOR MANAGERS · ROADMAP</div>
                <div className={`bracket cxos ${dimBracket(arch, "cxos")}`} data-b="cxos">FOR CXOS · ROADMAP</div>
              </div>
              <div className="arch-label">The memory</div>
              <div className="arch-memory">
                <div className="n">The tribal knowledge graph</div>
                <div className="a">Every agent reads from it and writes to it. Every fact cited. Compounds with every conversation.</div>
              </div>
            </div>
            <div className="arch-line reveal" id="archCaption">{cap.lead}<span>{cap.span}</span></div>
          </div>
        </section>

        <section style={{ padding: "40px 0 30px" }}>
          <div className="wrap">
            <div className="reveal">
              <div className="eyebrow blue">The memory</div>
              <h2>Every conversation<br />makes Shadow smarter.</h2>
              <p className="lede">Calls become typed, linked, cited knowledge that compounds for the whole team.</p>
            </div>
            <div className="graph-shell reveal">
              <div className="graph-frame">
                <svg viewBox="0 0 1020 440" xmlns="http://www.w3.org/2000/svg" aria-label="Shadow knowledge graph: conversations becoming connected facts">
                  <line className="gedge" x1="510" y1="215" x2="300" y2="112" style={{ animationDelay: ".2s" }} />
                  <line className="gedge" x1="510" y1="215" x2="262" y2="302" style={{ animationDelay: ".5s" }} />
                  <line className="gedge" x1="510" y1="215" x2="722" y2="104" style={{ animationDelay: ".8s" }} />
                  <line className="gedge" x1="510" y1="215" x2="768" y2="308" style={{ animationDelay: "1.1s" }} />
                  <line className="gedge" x1="300" y1="112" x2="262" y2="302" style={{ animationDelay: "1.4s" }} />
                  <line className="gedge" x1="722" y1="104" x2="768" y2="308" style={{ animationDelay: "1.7s" }} />
                  <line className="gedge" x1="510" y1="215" x2="510" y2="378" style={{ animationDelay: "2s" }} />
                  <g className="gfloat" style={{ animationDelay: ".1s" }}>
                    <rect x="432" y="186" width="156" height="58" rx="14" fill="#0E0E0C" />
                    <text x="510" y="210" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="14" fontWeight="600" fill="#FFFFFF">Acme Corp</text>
                    <text x="510" y="228" textAnchor="middle" fontFamily="IBM Plex Mono,monospace" fontSize="10" fill="#A3A39E">ACCOUNT · $48K ARR</text>
                  </g>
                  <g className="gfloat" style={{ animationDelay: ".6s" }}>
                    <rect x="212" y="84" width="176" height="56" rx="14" fill="#FFFFFF" stroke="#ECECE9" />
                    <circle cx="240" cy="112" r="12" fill="#E4E4F9" />
                    <text x="240" y="116" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="10" fontWeight="600" fill="#3A3BB0">SL</text>
                    <text x="262" y="108" fontFamily="Inter,sans-serif" fontSize="12.5" fontWeight="600" fill="#0E0E0C">Sarah Lin</text>
                    <text x="262" y="124" fontFamily="IBM Plex Mono,monospace" fontSize="9.5" fill="#A3A39E">VP ENG · CHAMPION</text>
                  </g>
                  <g className="gfloat" style={{ animationDelay: "1s" }}>
                    <rect x="158" y="274" width="208" height="56" rx="14" fill="#FFFFFF" stroke="#F5C4B3" />
                    <text x="178" y="298" fontFamily="Inter,sans-serif" fontSize="12.5" fontWeight="600" fill="#0E0E0C">Per-seat pricing pushback</text>
                    <text x="178" y="314" fontFamily="IBM Plex Mono,monospace" fontSize="9.5" fill="#993C1D">OBJECTION · RAISED 3X</text>
                  </g>
                  <g className="gfloat" style={{ animationDelay: "1.4s" }}>
                    <rect x="636" y="76" width="172" height="56" rx="14" fill="#FFFFFF" stroke="#9FE1CB" />
                    <text x="656" y="100" fontFamily="Inter,sans-serif" fontSize="12.5" fontWeight="600" fill="#0E0E0C">Send security docs</text>
                    <text x="656" y="116" fontFamily="IBM Plex Mono,monospace" fontSize="9.5" fill="#0F6E56">COMMITMENT · DUE FRI</text>
                  </g>
                  <g className="gfloat" style={{ animationDelay: "1.8s" }}>
                    <rect x="690" y="280" width="156" height="56" rx="14" fill="#FFFFFF" stroke="#FAC775" />
                    <text x="710" y="304" fontFamily="Inter,sans-serif" fontSize="12.5" fontWeight="600" fill="#0E0E0C">Competitor in play</text>
                    <text x="710" y="320" fontFamily="IBM Plex Mono,monospace" fontSize="9.5" fill="#854F0B">SIGNAL · RENEWAL NOV</text>
                  </g>
                  <g className="gfloat" style={{ animationDelay: "2.2s" }}>
                    <rect x="416" y="352" width="188" height="52" rx="14" fill="#FFFFFF" stroke="#4C4DDC" strokeDasharray="4 3" />
                    <text x="436" y="374" fontFamily="Inter,sans-serif" fontSize="12.5" fontWeight="600" fill="#3A3BB0">Budget confirmed: $48K</text>
                    <text x="436" y="390" fontFamily="IBM Plex Mono,monospace" fontSize="9.5" fill="#4C4DDC">NEW · FROM TODAY'S CALL</text>
                  </g>
                </svg>
                <div className="graph-feed" id="graphFeed"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}
